import { promptSections } from './ai/prompt-file';
import { findFactoryDispute } from './factory-dispute';
import { findFactoryQuestions } from './factory-questions';
import { findFactorySubtasks } from './factory-subtasks';
import { findFactorySuggestions } from './factory-suggestions';
import { findFactoryTasks } from './factory-tasks';

/**
 * SF-T-182: Jev tags each item a run step returns (a question, a suggestion, a proposed sub-task, a
 * disputed finding, a task it was asked to create) as a Product or an Architecture concern, so people
 * see which decisions go to product engineers or owners and which to architectural engineers. The
 * question's wording is in factory/ai-prompts/decision-concern.md; this collects the items, builds one
 * choice question per item and reads the answers back. Pure, so it's tested without a call
 * (worker/src/services/runs/concern.ts makes it).
 */

export const DECISION_CONCERNS = ['product', 'architecture'] as const;
export type DecisionConcern = (typeof DECISION_CONCERNS)[number];

export const DECISION_CONCERN_LABELS: Record<DecisionConcern, string> = { product: 'Product', architecture: 'Architecture' };

/** Below this, an item gets no tag. */
export const DECISION_CONCERN_MIN_CONFIDENCE = 0.65;

export interface ConcernTag {
  concern: DecisionConcern;
  confidence: number;
}

/** The tags on a message (its event's `payload.concerns`), by item key (concernKey). */
export type ConcernTags = Record<string, ConcernTag>;

export type ConcernItemKind = 'question' | 'suggestion' | 'subtask' | 'dispute' | 'task';

/**
 * An item's key in `payload.concerns`: a question's, sub-task's, disputed finding's or created task's
 * place in its block (1-based), a suggestion's id (what its card and decisions go by).
 */
export const concernKey = (kind: ConcernItemKind, which: number | string) => `${kind}:${which}`;

export interface ConcernItem {
  key: string;
  kind: ConcernItemKind;
  title: string;
  detail: string;
}

const oneLine = (s: string | undefined) => (s ?? '').replace(/\s+/g, ' ').trim();
/** Each item's detail Jev reads, at most (a created task's description can be 8000). */
const DETAIL_MAX = 1500;
const clip = (s: string) => (s.length > DETAIL_MAX ? `${s.slice(0, DETAIL_MAX - 1).trimEnd()}…` : s);

/** Whether a message holds any block whose items are tagged. Cheap: no parsing. */
export function hasConcernBlocks(message: string): boolean {
  return /^ {0,3}(?:`{3,}|~{3,})[ \t]*factory-(?:questions|suggestions|subtasks|dispute|tasks)[ \t]*$/m.test(message);
}

/** Every item in a message's blocks, keyed. A dispute that only says the builder's verdict is unclear has nothing to tag. */
export function concernItems(message: string): ConcernItem[] {
  const items: ConcernItem[] = [];
  findFactoryQuestions(message)?.questions.questions.forEach((q, i) => {
    const options = q.options.map((o) => `${oneLine(o.label)}${o.description ? ` (${oneLine(o.description)})` : ''}`).join('; ');
    items.push({
      key: concernKey('question', i + 1),
      kind: 'question',
      title: `${q.header ? `${oneLine(q.header)}: ` : ''}${oneLine(q.question)}`,
      detail: clip(options ? `Options: ${options}` : ''),
    });
  });
  findFactorySuggestions(message)?.suggestions.suggestions.forEach((s) => {
    items.push({ key: concernKey('suggestion', s.id), kind: 'suggestion', title: oneLine(s.title), detail: clip(oneLine(s.detail)) });
  });
  findFactorySubtasks(message)?.subtasks.subtasks.forEach((s, i) => {
    items.push({ key: concernKey('subtask', i + 1), kind: 'subtask', title: oneLine(s.title), detail: clip(oneLine(s.detail)) });
  });
  const dispute = findFactoryDispute(message);
  if (dispute && !dispute.unclear) {
    dispute.findings.forEach((f, i) => {
      const detail = [f.where ? `Where: ${oneLine(f.where)}` : '', f.reason ? `The builder disputes it: ${oneLine(f.reason)}` : ''].filter(Boolean).join('. ');
      items.push({ key: concernKey('dispute', i + 1), kind: 'dispute', title: oneLine(f.title), detail: clip(detail) });
    });
  }
  findFactoryTasks(message)?.tasks.forEach((t, i) => {
    items.push({ key: concernKey('task', i + 1), kind: 'task', title: oneLine(t.title), detail: clip(oneLine(t.description)) });
  });
  return items;
}

export interface ConcernPrompt {
  instructions: string;
  criteria: Record<DecisionConcern, string>;
}

/** The prompt file's `## concern` section; throws naming what's missing, so a bad file fails its test. */
export function parseConcernPrompt(body: string): ConcernPrompt {
  const section = promptSections(body).concern;
  if (!section?.instructions) throw new Error('decision-concern.md: the "## concern" section or its instructions are missing');
  const map = Object.fromEntries(section.criteria.map((c) => [c.key, c.text]));
  const missing = DECISION_CONCERNS.filter((c) => !map[c]);
  if (missing.length) throw new Error(`decision-concern.md: "## concern" has no line for ${missing.join(', ')}`);
  return { instructions: section.instructions, criteria: { product: map.product, architecture: map.architecture } };
}

const ITEM_LABELS: Record<ConcernItemKind, string> = {
  question: 'A question for people',
  suggestion: 'A suggestion',
  subtask: 'A proposed sub-task',
  dispute: 'A review finding the builder disputes',
  task: 'A task the run was asked to create',
};

/** Jev's key for the item at `index` in the list (`i1`, `i2`, …): item keys hold `:`, which Jev's keys needn't. */
const jevKey = (index: number) => `i${index + 1}`;

/** One choice question per item, keyed i1, i2, … in the items' order. */
export function concernQuestions(prompt: ConcernPrompt, items: ConcernItem[]): Record<string, { type: 'choice'; instructions: string; criteria: Record<DecisionConcern, string> }> {
  return Object.fromEntries(
    items.map((item, i) => [
      jevKey(i),
      {
        type: 'choice' as const,
        instructions: `${prompt.instructions}\n\n${ITEM_LABELS[item.kind]}: ${item.title}${item.detail ? `\n${item.detail}` : ''}`,
        criteria: { ...prompt.criteria },
      },
    ])
  );
}

/** Jev's answers as tags, by item key: only the ones it's sure enough of. */
export function readConcerns(answers: Record<string, unknown>, items: ConcernItem[], minConfidence = DECISION_CONCERN_MIN_CONFIDENCE): ConcernTags {
  const tags: ConcernTags = {};
  items.forEach((item, i) => {
    const a = (answers[jevKey(i)] ?? {}) as { choice?: unknown; confidence?: unknown };
    const confidence = typeof a.confidence === 'number' ? a.confidence : 0;
    if (!isDecisionConcern(a.choice) || confidence < minConfidence) return;
    tags[item.key] = { concern: a.choice, confidence: Math.round(confidence * 1000) / 1000 };
  });
  return tags;
}

export function isDecisionConcern(value: unknown): value is DecisionConcern {
  return typeof value === 'string' && (DECISION_CONCERNS as readonly string[]).includes(value);
}

/** A message's tags from its event's payload; empty when it has none (a message from before SF-T-182) or they're malformed. */
export function payloadConcerns(payload: unknown): ConcernTags {
  const raw = payload && typeof payload === 'object' ? (payload as { concerns?: unknown }).concerns : null;
  if (!raw || typeof raw !== 'object') return {};
  const tags: ConcernTags = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const v = (value ?? {}) as { concern?: unknown; confidence?: unknown };
    if (isDecisionConcern(v.concern)) tags[key] = { concern: v.concern, confidence: typeof v.confidence === 'number' ? v.confidence : 0 };
  }
  return tags;
}

/** The `#tag` a created task's description carries for its concern. */
export const concernTag = (concern: DecisionConcern) => `#${concern}`;

/**
 * SF-T-159: a batch's questions are split between the larger task and its sub-tasks, each getting a
 * round of its own, numbered from 1. The tags of `round`'s questions, keyed by their place in it, from
 * the tags of the block they came from (matched by id).
 */
export function concernsForRound(tags: ConcernTags, from: Array<{ id: string }>, round: Array<{ id: string }>): ConcernTags {
  const out: ConcernTags = {};
  round.forEach((q, i) => {
    const at = from.findIndex((o) => o.id === q.id);
    const tag = at >= 0 ? tags[concernKey('question', at + 1)] : undefined;
    if (tag) out[concernKey('question', i + 1)] = tag;
  });
  // Anything else in the message (suggestions and the like) keeps its tag.
  for (const [key, tag] of Object.entries(tags)) if (!key.startsWith('question:')) out[key] = tag;
  return out;
}
